import { useState, useEffect } from "react";
import { Modal } from "@/components/ui/modal";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { UploadMultiImages } from "@/components/ui/upload-multi-images";
import { UploadMultiFiles } from "@/components/ui/upload-multi-files";
import { FoodDrinksActivity } from "../models/activity.model";
import { Link } from "lucide-react";

interface FoodDrinksModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (activity: Partial<FoodDrinksActivity>) => void;
  icon?: React.ReactNode;
  initialData?: Partial<FoodDrinksActivity>;
}

const FoodDrinksModal = ({ isOpen, onClose, onSave, icon, initialData }: FoodDrinksModalProps) => {
  const [formData, setFormData] = useState<Partial<FoodDrinksActivity>>({
    title: '',
    description: '',
    link: '',
    media: [],
    documentation: []
  });

  // Update form data when initialData changes
  useEffect(() => {
    if (initialData) {
      setFormData(initialData);
    }
  }, [initialData]);

  const [errors, setErrors] = useState<Record<string, boolean>>({});

  const validateForm = () => {
    const requiredFields = ['title', 'description', 'link'];
    const newErrors: Record<string, boolean> = {};

    requiredFields.forEach(field => {
      const value = formData[field as keyof FoodDrinksActivity];
      newErrors[field] = !value || (typeof value === 'string' && value.trim() === '');

    });

    setErrors(newErrors);
    return !Object.values(newErrors).some(error => error);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      onSave({
        ...formData,
        type: 'food-drinks' as const
      });
      onClose();
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={
        <div className="flex items-center gap-2">
          {icon} Food & Drinks
        </div>
      }
      size="lg"
      footer={
        <div className="flex w-full justify-between">
          <Button type="button" size="sm" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" size="sm" form="food-drinks-form">
            Confirm
          </Button>
        </div>
      }
    >
      <form id="food-drinks-form" onSubmit={handleSubmit} className="space-y-4 p-6">
        {/* Title Card */}
        <Card>
          <CardHeader>
            <CardTitle>Title</CardTitle>
          </CardHeader>
          <CardContent>
            <Input
              id="title"
              value={formData.title || ''}
              onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
              placeholder="Restaurant/Place Name"
              hasError={errors.title}
            />
          </CardContent>
        </Card>

        {/* Description Card */}
        <Card>
          <CardHeader>
            <CardTitle>Description</CardTitle>
          </CardHeader>
          <CardContent>
            <Textarea
              id="description"
              value={formData.description || ''}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Describe the restaurant or dining experience..."
              rows={6}
              hasError={errors.description}
            />
          </CardContent>
        </Card>

        {/* Media, Documentation and Link Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
          {/* Media Card */}
          <Card>
            <CardHeader>
              <CardTitle>Media</CardTitle>
            </CardHeader>
            <CardContent>
              <UploadMultiImages
                images={formData.media || []}
                onImagesChange={(images) => setFormData(prev => ({ ...prev, media: images }))}
                label="Upload 4 photos (optional)"
                maxImages={10}
              />
            </CardContent>
          </Card>

          {/* Documentation Card */}
          <Card>
            <CardHeader>
              <CardTitle>Documentation</CardTitle>
            </CardHeader>
            <CardContent>
              <UploadMultiFiles
                files={formData.documentation || []}
                onFilesChange={(files) => setFormData(prev => ({ ...prev, documentation: files }))}
                label="Upload supporting documentation"
                maxFiles={5}
              />
            </CardContent>
          </Card>

          {/* Link Card */}
          <Card>
            <CardHeader>
              <CardTitle>Link</CardTitle>
            </CardHeader>
            <CardContent>
              <label htmlFor="link" className="text-xs">Add a supporting link for the client</label>
              <div className="relative mt-3">
                <Link className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-900" />
                <Input
                  id="link"
                  type="url"
                  value={formData.link || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, link: e.target.value }))}
                  placeholder="link"
                  className="pl-10"
                  hasError={errors.link}
                />
              </div>
            </CardContent>
          </Card>
        </div>
      </form>
    </Modal>
  );
};

export default FoodDrinksModal;
